// React imports
import React, { useState, useEffect, useRef, useMemo } from 'react';

// Generic imports
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import pluralize from 'pluralize';
import oxfordJoin from 'oxford-join';
import axios from 'axios';

// Mui imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import DialogContentText from '@mui/material/DialogContentText';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { useTheme } from '@mui/material/styles';

// Fontawesome icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { red } from '@mui/material/colors';
import { faPencil, faWarning, faMagnifyingGlass, faChainBroken, faCircle as faCircleLight, faCheck, faXmark, faFilePdf, faPaperclip, faDownload, faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';

// Local imports
import currencyToText from '../../../CurrencyToText.js';
import { calcFieldValue } from '../../../utils/opportunity.js';
import { minimizeProducts, maximizeProducts, getErrors } from '../../utils/opportunity.js';
import { addMarginToFormula } from '../../../utils/Formula.js';
import { findMatchingProducts } from '../../utils/product.js';
import { getTotalRates, convertRateKgToLbsIfNeeded } from '../../utils/rates.js';
import OpportunityEditor from './OpportunityOneOffFreeTextBuilder.editor.js';
import FieldSettingsDialog from '../OpportunityOneOffEmailBuilder.fielddialog.js';
import { FieldTypes } from '../../../FieldTypes.js';
import AddTemplate from '../OpportunityOneOffBuilder.addtemplate.js';
import readableId from '../../../ReadableId.js';
import TermsDialog from '../OpportunityOneOffBuilder.terms.js';
import getBaseUrl from '../../../GetBaseUrl.js';
import { quotePdfFilename } from '../../utils/downloadQuotePdf.js';
import { useQuotePdfDownload, QuotePdfNotice } from '../../components/QuotePdfDownload.js';
import StructurePane from '../../sidepane/SidePane.structure.js';
import { offerToText } from '../OpportunityOneOffEmailBuilder.utils.js';

function OpportunityOneOffFreeTextBuilder(props) {
    const theme = useTheme();
    const { transportationMethod, opportunityId, org: organization, exchangeRates, lockedExchangeRates, accounts,
        offers, setOffers, preface, setPreface, products, suppliers, buyingRates, setBuyingRates,
        routes, opportunities, terms, setTerms, setTransportationMethod, setProducts, setRoutes, status,
        saveSuccess, saving, saveError, saveErrorDescription, onSave, onSaveNow, dirty, accesstoken, incoterm, attachPDF, setAttachPDF,
        account, opportunity, onOpenFullScreen, onFormBuilder, isOwner } = props;
    const [ dialogs, setDialogs ] = useState([]);
    const [ view, setView ] = useState('editor');

    // The PDF is rendered server-side from the customer-facing page, so this takes seconds -
    // and from the *saved* quote, so an edit made here has to reach the server first. Dragging
    // a rate into a section marks the quote dirty but only schedules a save; downloading in
    // the meantime used to hand back a PDF without it.
    const { downloadingPdf, pdfError, downloadPdf: onDownloadPdf, notice: pdfNotice, dismissNotice: dismissPdfNotice } =
        useQuotePdfDownload({ opportunity, opportunityId, accesstoken, saveBeforeDownload: onSaveNow, hasUnsavedChanges: dirty });

    // Host a pasted screenshot rather than carrying it inline: an opportunity is stored as
    // one JSON blob, and a base64 screenshot inside it would count against that.
    async function onUploadImage({ dataUrl, contentType }) {
        const { data } = await axios.post(
            `/api/users/signature-image?accesstoken=${accesstoken}&organizationId=${organization.id}`,
            { image: dataUrl, contentType, prefix: 'quote-images' }
        );
        return data?.url;
    }

    const editorRef = useRef(null);
    const setDialogsRef = useRef(null);
    setDialogsRef.current = setDialogs;

    // Auto-populate default terms when opportunity is first loaded
    const hasAutoPopulatedRef = useRef(false);
    const lastTransportationMethodRef = useRef(transportationMethod);
    
    useEffect(() => {
        // Reset the ref if transportation method changed from undefined/null to a value
        // This allows auto-population to happen when transportation method is set later
        if (!lastTransportationMethodRef.current && transportationMethod) {
            hasAutoPopulatedRef.current = false;
        }
        lastTransportationMethodRef.current = transportationMethod;
    }, [transportationMethod]);
    
    useEffect(() => {
        // Only auto-populate once per opportunity load (unless transportation method was just set)
        if (hasAutoPopulatedRef.current) return;
        
        // Auto-populate if terms is null/undefined or empty array
        // This will happen for new opportunities that haven't had terms set yet
        const hasNoTerms = terms === null || terms === undefined || (Array.isArray(terms) && terms.length === 0);
        
        if (!hasNoTerms) {
            hasAutoPopulatedRef.current = true; // Mark as processed even if we don't auto-populate
            return;
        }
        if (!transportationMethod) return; // Need transportation method to know which terms to use
        if (!organization?.terms) return;
        
        // Auto-populate default terms based on transportation method
        let newTerms = null;
        if (organization.terms[`price-quote-${transportationMethod}`]) {
            newTerms = [{ name: `price-quote-${transportationMethod}`, value: organization.terms[`price-quote-${transportationMethod}`] }];
        } else if (organization.terms['price-quote']) {
            newTerms = [{ name: 'price-quote', value: organization.terms['price-quote'] }];
        }
        
        if (newTerms) {
            hasAutoPopulatedRef.current = true;
            setTerms(newTerms);
        } else {
            hasAutoPopulatedRef.current = true; // Mark as processed even if no terms found
        }
    }, [terms, transportationMethod, organization?.terms, setTerms]);
    
    // Reset the ref when opportunity changes
    useEffect(() => {
        hasAutoPopulatedRef.current = false;
        lastTransportationMethodRef.current = transportationMethod;
    }, [opportunityId, transportationMethod]);

    useEffect(() => {
        if (!products) return;
        if (!editorRef.current) return;

        const allFields = _(offers).map(o => o.sections).flatten().map(s => s.offers).flatten().map(o => o.fields).flatten().value();
        for (const field of allFields) {
            updateFieldValues({ offers, field, products, transportationMethod, exchangeRates, lockedExchangeRates, organization, buyingRates, editorRef });
        }
        setTimeout(() => {
            // Update the totals after we are sure the editor has been updated
            if (editorRef?.current) {
                editorRef.current.requestUpdateTotals();
            }
        }, 500);
    }, [JSON.stringify(products)]);


    // Update editor when field titles change in offers (e.g., from structure pane)
    // Extract field titles for dependency tracking
    const fieldTitlesMap = useMemo(() => {
        const allFields = _(offers).map(o => o.sections).flatten().map(s => s.offers).flatten().map(o => o.fields).flatten().value();
        return allFields.reduce((acc, f) => {
            acc[f.id] = f.title || '';
            return acc;
        }, {});
    }, [offers]);

    // Serialize buying rate references on offer fields so the effect fires when links change
    const fieldBuyingRatesKey = useMemo(() => {
        const allFields = _(offers).map(o => o.sections).flatten().map(s => s.offers).flatten().map(o => o.fields).flatten().value();
        return JSON.stringify(allFields.map(f => ({ id: f.id, br: f.buyingRates })));
    }, [offers]);

    useEffect(() => {
        if (!editorRef.current) return;

        const allOfferFields = _(offers).map(o => o.sections).flatten().map(s => s.offers).flatten().map(o => o.fields).flatten().value();
        const editorFieldIds = editorRef.current.getFieldIds?.() || [];

        const offerFieldsById = _.keyBy(allOfferFields, 'id');

        // Update editor nodes
        for (const editorFieldId of editorFieldIds) {
            const field = offerFieldsById[editorFieldId];
            if (field) {
                updateFieldValues({ offers, field, products, transportationMethod, exchangeRates, lockedExchangeRates, organization, buyingRates, editorRef });
            } else {
                // Editor node exists but not in offers (e.g., removed after unlink) — clear stale cost data
                const zeroCurrency = currencyToText(organization?.defaults?.currency || 'USD', true, '0');
                editorRef.current.updateFieldValue(editorFieldId, zeroCurrency, 0, zeroCurrency, '', '', '', '');
            }
        }

        setTimeout(() => {
            editorRef.current?.requestUpdateTotals();
        }, 500);
    }, [JSON.stringify(fieldTitlesMap), fieldBuyingRatesKey, offers, products, transportationMethod, exchangeRates, lockedExchangeRates, organization, buyingRates]);

    function onFieldClicked({ id, update }) {
        const allFields = _(offers).map(o => o.sections).flatten().map(s => s.offers).flatten().map(o => o.fields).flatten().value();
        let field = allFields.find(field => field.id === id);
        if (!field) {
            field = onFieldCreated({ id });
        }

        if (!update) {
            update = (value) => {
                editorRef.current?.updateFieldValue(id, value, 123, '', '', '', '', '');
            }
        }
        setDialogs(d => [...d, { id: uuidv4(), open: 'field', field, update }]);
    }

    function onFieldCreated({ id, title, type, value, currency, showDialog, update }) {
        const values = {};
        if (type && value && currency) {
            const fieldType = (FieldTypes[transportationMethod] || []).find(ft => ft.value === type);
            const chargeLines = fieldType?.getChargeLines ? fieldType.getChargeLines({ products, organization }) : [];
            const fieldValueId = chargeLines ? chargeLines[0].fieldValueId : type;
            values[fieldValueId] = { value, currency };
        }

        const field = {
            id,
            title: title || 'Unnamed Field',
            type: type || 'flat',
            values: values
        };

        setOffers(offers => {
            const _offers = _.cloneDeep(offers);
            const offer = (() => {
                if (_offers.length > 0) return _offers[0];
                const newOffer = {
                    id: uuidv4(),
                    title: 'New Offer',
                    sections: [{
                        id: uuidv4(),
                        title: '***UNASSIGNED***',
                        offers: [{ id: uuidv4(), fields: [] }]
                    }]
                };

                ['Origin', 'Freight', 'Destination', 'Inland', 'Additional Charges'].forEach(title => {
                    newOffer.sections.push({
                        id: uuidv4(),
                        title,
                        description: '',
                        offers: [{
                            id: uuidv4(),
                            fields: [],
                        }]
                    });
                });

                _offers.push(newOffer);
                return newOffer;
            })();

            offer.sections[0].offers[0].fields.push(field);
            return _offers;
        });

        if (update) {
            setDialogs(d => [...d, { id: uuidv4(), open: 'field', field, update }]);
        }

        return field;
    }

    function onFieldUpdated({ field, section, update, buyingRates, offers }) {
        if (!update) return;
        if (field.type === 'string') {
            update(field.values?.string?.value || '');
            return;
        }
        const _field = _.cloneDeep(field);
        _field.displayCurrency = _field.displayCurrency?.convert ? _field.displayCurrency.to : (_.isString(_field.displayCurrency) ? _field.displayCurrency : null);

        const summary = getSummaryForField({ field: _field, offers, products, buyingRates, transportationMethod, exchangeRates, lockedExchangeRates, organization });

        const sectionTitle = offers.map(o => o.sections).flat().find(s => s.id === section)?.title || '***UNASSIGNED***';

        update(summary.str, summary.margin, summary.profitStr, summary.costStr, summary.suppliersStr, field.title, sectionTitle === '***UNASSIGNED***' ? '' : sectionTitle);

        const allFields = _(offers).map(o => o.sections).flatten().map(s => s.offers).flatten().map(o => o.fields).flatten().value();
        for (const field of allFields) {
            updateFieldValues({ offers, field, products, transportationMethod, exchangeRates, lockedExchangeRates, organization, buyingRates, editorRef });
        }
    }

    function onTotalNeedsUpdate({ fields, callback }) {
        const { str, margin, profitStr, costStr, suppliersStr } = getTotalForFields({ fields, offers, products, buyingRates, transportationMethod, exchangeRates, lockedExchangeRates, organization });
        callback(str, fields, margin, profitStr, costStr, suppliersStr);
    }

    function onTotalFieldCreated({ id }) {
        const field = onFieldCreated({ id });
        setDialogs(d => [...d, { id: uuidv4(), open: 'field', field, update: (field) => {} }]);
    }

    function onFieldsCreatedFromRate({ rate, fields, idConversions, callbackValues }) {
        // Convert rate from KG to LBS if needed before using its values
        const convertedRate = convertRateKgToLbsIfNeeded({ rate, opportunityProducts: products });
        setBuyingRates(_.uniqBy([...buyingRates, convertedRate ], 'id'));

        async function onMargin({ margin, addMetaData, offerType, newOfferName, existingOffer, canceled }) {

            if (canceled) {
                // TODO: Erase fields from free text
                return;
            }

            const _offers = _.cloneDeep(offers);
            const offer = (() => {
                if (offerType === 'existing') {
                    const offer = _offers.find(o => o.id === existingOffer);
                    return offer;
                }
                const newOffer = {
                    id: uuidv4(),
                    title: newOfferName || 'New Offer',
                    sections: [{
                        id: uuidv4(),
                        title: '***UNASSIGNED***',
                        offers: [{ id: uuidv4(), fields: [] }]
                    }]
                };
                _offers.push(newOffer);
                return newOffer;
            })();

            const sectionOffer = offer.sections[0].offers[0];
            let matchingProducts = findMatchingProducts({ product: convertedRate.product, products });
            let _products = products;

            if (_.isEmpty(matchingProducts)) {
                const newProduct = minimizeProducts({
                    products: maximizeProducts({
                        products: [{ ...convertedRate.product, id: uuidv4() }]
                    }),
                    transportationMethod: convertedRate.transportationMethod
                })[0];
                _products = [..._products, newProduct];
                setProducts(_products);
                matchingProducts = [newProduct];
            }

            let _transportationMethod = transportationMethod;
            if (!transportationMethod) {
                setTransportationMethod(convertedRate.transportationMethod);
                _transportationMethod = convertedRate.transportationMethod;
            }

            if (convertedRate.source && convertedRate.destination) {
                if (!routes || !routes.find(r => ((r.source || convertedRate.source).id === convertedRate.source.id && (r.destination || convertedRate.destination).id === convertedRate.destination.id))) {
                    setRoutes([...routes || [], { source: convertedRate.source, destination: convertedRate.destination }]);
                }
            }

            const newFields = (fields || []).map(f => {
                // Find the converted field from the converted rate
                const convertedField = (() => {
                    for (const s of convertedRate.offer.sections) {
                        for (const o of s.offers) {
                            const field = o.fields.find(field => field.id === f.id);
                            if (field) return field;
                        }
                    }
                    return f;
                })();
                
                const fieldType = (FieldTypes[convertedRate.transportationMethod || _transportationMethod] || []).find(ft => ft.value === convertedField.type);
                const chargeLines = fieldType?.getChargeLines ? fieldType.getChargeLines({ field: convertedField, products: [convertedRate.product], organization }) : [];
                const fieldValueId = chargeLines ? chargeLines?.[0]?.fieldValueId || convertedField.type : convertedField.type;

                const chargeLinesNew = fieldType?.getChargeLines ? _.flatten(matchingProducts.map(product => fieldType.getChargeLines({ field: convertedField, products: [product], organization }))) : [];
                const fieldValueIdsNew = _.uniq(_.compact(chargeLines ? chargeLinesNew.map(cl => cl.fieldValueId || convertedField.type) : convertedField.type));

                const originalValue = convertedField.values?.[fieldValueId]?.value;
                const newValue = fieldValueId === 'string' 
                    ? originalValue 
                    : null;

                // Check if field type is enabled, if not change to 'per-unit'
                let _fieldType = convertedField.type === 'per-unit' ? 'per-unit-type' : convertedField.type;
                const fieldTypeConfig = FieldTypes[convertedRate.transportationMethod]?.find(ft => ft.value === _fieldType);
                if (fieldTypeConfig?.enabled && !fieldTypeConfig.enabled({ organization })) {
                    _fieldType = 'per-unit';
                }
                
                const newField = {
                    ...convertedField,
                    type: _fieldType,
                    id: idConversions[f.id],
                    values: fieldValueIdsNew.reduce((acc, fieldValueId) => {
                        acc[fieldValueId] = {
                            ...convertedField.values[fieldValueId],
                            ...(newValue != null ? { value: newValue } : {}),
                            ...{ formula: `max(0,buying * ${1 + margin})` }
                        };
                        return acc;
                    }, {}),
                    buyingRates: matchingProducts.map(product => ({
                        rateId: convertedRate.id,
                        fieldId: f.id,
                        opportunityProductId: product.id,
                    }))
                }

                const rateSectionName = _.find(convertedRate.offer.sections, s => s.offers[0].fields.find(field => field.id === f.id))?.title;
                const section = (() => {
                    if (!rateSectionName) return offer.sections[0];

                    const existingSection = offer.sections.find(s => s.title === rateSectionName);
                    if (existingSection) return existingSection;

                    const newSection = {
                        id: uuidv4(),
                        title: rateSectionName,
                        offers: [{ id: uuidv4(), fields: [] }]
                    };
                    offer.sections.push(newSection);
                    return newSection;
                })();
                section.offers[0].fields.push(newField);

                return newField;
            })

            await setOffers(_offers);

            const _buyingRates = _.uniqBy([...buyingRates, convertedRate], 'id');
            callbackValues({
                fields: newFields.map(f => {
                    const summary = getSummaryForField({
                        field: f,
                        offers: _offers,
                        products: _products,
                        buyingRates: _buyingRates,
                        transportationMethod: _transportationMethod,
                        exchangeRates,
                        lockedExchangeRates,
                        organization
                    });
                    return {
                        id: f.id,
                        value: _.map(summary.sum, (v, k) => currencyToText(k, true, v.toLocaleString({ maximumFractionDigits: 2 }))).join(', '),
                        margin: summary.margin,
                        cost: summary.costStr,
                        profit: summary.profitStr,
                        suppliers: summary.suppliersStr
                    }
                }),
                addMetaData
            });

        }

        setDialogs(d => [...d, { id: uuidv4(), open: 'addrate', onMargin, rate: convertedRate }]);
    }

    function onBlur({ html }) {
        setPreface(html);
    }

    const [deleteFieldConfirm, setDeleteFieldConfirm] = useState(null);

    function onFieldDeleted({ id }) {
        // Remove field from offers structure
        setOffers(offers => {
            const _offers = _.cloneDeep(offers);
            _offers.forEach(offer => {
                offer.sections.forEach(section => {
                    section.offers.forEach(offerItem => {
                        offerItem.fields = offerItem.fields.filter(f => f.id !== id);
                    });
                });
            });
            return _offers;
        });
    }

    function onFieldDeleteRequested({ id, ids, fields }) {
        const deletedIds = ids || [id];
        const deletedFields = fields || [];

        // Get field IDs used in totals that still exist in the editor
        const totalFieldIds = editorRef.current?.getTotalFieldIds?.() || [];

        // Categorize each deleted field
        const sectionFields = [];
        const totalFields = [];
        const deletableFields = [];

        deletedIds.forEach(fieldId => {
            const fieldInfo = deletedFields.find(f => f.id === fieldId);
            const label = fieldInfo?.label || fieldId;

            // Check if field is assigned to a non-default section
            const section = offers.flatMap(o => o.sections).find(s =>
                s.offers.some(so => so.fields.some(f => f.id === fieldId))
            );
            const isAssignedToSection = section && section.title !== '***UNASSIGNED***';

            // Check if field is used in a remaining total
            const isUsedInTotal = totalFieldIds.includes(fieldId);

            if (isAssignedToSection) {
                sectionFields.push({ id: fieldId, label });
            } else if (isUsedInTotal) {
                totalFields.push({ id: fieldId, label });
            } else {
                deletableFields.push({ id: fieldId, label });
            }
        });

        // If all fields are freely deletable (none assigned to sections or used in totals), just delete them
        if (sectionFields.length === 0 && totalFields.length === 0) {
            deletableFields.forEach(f => onFieldDeleted({ id: f.id }));
            return;
        }

        setDeleteFieldConfirm({ ids: deletedIds, sectionFields, totalFields, deletableFields });
    }

    function onTotalCreated({ id, selectedFieldIds }) {
        return new Promise((resolve, reject) => {
            const allFields = _(offers).map(o => o.sections).flatten().map(s => s.offers).flatten().map(o => o.fields).flatten().value();
            const disconnectedFields = allFields.filter(f => {
                if (!(_(offers).map(o => o.sections?.[0].offers?.[0].fields).flatten().value().map(f => f.id).includes(f.id))) return false;
                return !(preface || '').toLowerCase().includes(f.id.toLowerCase());
            }).map(f => f.id);
            const checkedFields = selectedFieldIds ? selectedFieldIds : allFields.map(f => f.id).filter(f => !disconnectedFields.includes(f));   
            setDialogs(d => [...d, { id: uuidv4(), open: 'total', total: { id, checkedFields, update: (sum, checkedFields, margin, profitStr, costStr, suppliersStr) => {
                if (!sum && !checkedFields) {
                    reject();
                } else {
                    resolve({ sum, checkedFields, margin, profitStr, costStr, suppliersStr });
                }
            } } }]);
        })
    }

    function onTotalClicked({ id, checkedFields, update }) {
        setDialogs(d => [...d, { id: uuidv4(), open: 'total', total: { id, checkedFields, update } }]);
    }

    function onAddTemplate({ callback }) {
        setDialogs(d => [...d, { id: uuidv4(), open: 'add-template', callback: ({ preface, newOffers }) => {
            callback(preface);
            // Get all field ids from the preface
            const fieldIds = _.uniq(preface.match(/"[0-9a-f-]{36}"/g).map(id => id.replace(/"/g, '')));
            // For each id, find the field in the offers
            const allFields = newOffers.flatMap(o => o.sections).flatMap(s => s.offers).flatMap(so => so.fields);
            fieldIds.forEach(id => {
                const _field = allFields.find(f => f.id === id);
                if (!_field) return;
                updateFieldValues({ offers: newOffers, field: _field, products, transportationMethod, exchangeRates, lockedExchangeRates, organization, buyingRates, editorRef });
            });
            setTimeout(() => {
                // Update the totals after we are sure the editor has been updated
                editorRef.current?.requestUpdateTotals();
            }, 500);
        }}]);
    }

    const templates = _.uniqBy([
        ..._.values(_.pickBy(organization.forms, f => !f.deleted &&
        f.type === 'opportunity-email-template' &&
        (!f.transportationMethods || f.transportationMethods.includes(transportationMethod)))),
        ...((opportunities || []).filter(o => o.attributes?.template && o.transportationMethod === transportationMethod && o.type === 'one-off-free-text'))], 'id');

    // Compute validation errors for preview
    const validationErrors = useMemo(() => {
        // Compute errors even if opportunity/account are not fully loaded
        const _opportunity = {
            type: 'one-off-free-text',
            account: account ? _.pick(account, ['id']) : {},
            incoterm: incoterm || null,
            products: products ? minimizeProducts({ products }) : [],
            transportationMethod: transportationMethod || null,
            routes: routes || [],
            preface: preface || null,
            offers: offers || [],
            buyingRates: (buyingRates || []).map(br => {
                if (!br.product || !products) return br;
                return { ...br, product: minimizeProducts({ products: [br.product] })[0] };
            }),
            exchangeRates: lockedExchangeRates || {},
        };
        const er = getErrors({ opportunity: _opportunity });
        const allErrors = _.values({ ..._.mapValues(er, (e,k) => e.map(ee => ({ ...ee, id: `${k}:${ee.id}` }))), header: [] }).flat();
        return allErrors;
    }, [account, incoterm, products, transportationMethod, routes, preface, offers, buyingRates, lockedExchangeRates]);

    // Error fix map for navigation
    const ERROR_FIX_MAP = {
        'account:id': () => {
            // Scroll to account selector - this would need to be implemented based on your UI structure
            console.log('Fix account');
        },
        'incoterm:transportationMethod': () => {
            console.log('Fix transportation method');
        },
        'incoterm:incoterm': () => {
            console.log('Fix incoterm');
        },
    };

    return (
        <Box sx={{ background: '#F8FAFC', paddingBottom: '24px', position: 'relative' }}>
            <Paper sx={{ backgroundColor: '#FFFFFF', borderRadius: '16px', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', mb: 3, overflow: 'hidden' }}>
                <Box sx={{ background: 'transparent', padding: '6px 24px', paddingTop: '24px', mb: 3, borderBottom: '1px solid rgba(59, 130, 246, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant='body1' sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' }}>
                        Quick Quote Builder
                        {opportunity?.created && (
                            <Box component='span' sx={{ ml: 1, color: '#94a3b8', fontWeight: 500 }}>· #{readableId(opportunity)}</Box>
                        )}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {onOpenFullScreen && (
                            <Tooltip title='Open as Quick Quote in a new tab' arrow placement='top'>
                                <Button
                                    size='small'
                                    onClick={onOpenFullScreen}
                                    sx={{ textTransform: 'none', fontSize: '11px', color: '#64748b', minWidth: 0, '&:hover': { color: '#3b82f6' } }}
                                >
                                    <FontAwesomeIcon icon={faMagnifyingGlass} style={{ marginRight: 4 }} />
                                    Open Fullscreen
                                </Button>
                            </Tooltip>
                        )}
                        {onFormBuilder && isOwner && (
                            <Tooltip title='Convert to Form Builder and open in a new tab — gives more layout and formatting options' arrow placement='top'>
                                <Button
                                    size='small'
                                    onClick={onFormBuilder}
                                    sx={{ textTransform: 'none', fontSize: '11px', color: '#64748b', minWidth: 0, '&:hover': { color: '#3b82f6' } }}
                                >
                                    <FontAwesomeIcon icon={faPencil} style={{ marginRight: 4 }} />
                                    Open in Form Builder
                                </Button>
                            </Tooltip>
                        )}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px', gap: 0, backgroundColor: '#F1F5F9', borderRadius: '9999px', margin: '16px', maxWidth: 'fit-content', marginLeft: 'auto' }}>
                    <Button 
                        onClick={() => setView('editor')}
                        sx={{ 
                            minWidth: '100px', 
                            padding: '6px 16px', 
                            fontSize: '13px', 
                            fontWeight: 500,
                            borderRadius: view === 'editor' ? '9999px' : '9999px',
                            textTransform: 'none',
                            fontFamily: 'Poppins, sans-serif',
                            backgroundColor: view === 'editor' ? '#FFFFFF' : 'transparent',
                            color: view === 'editor' ? '#4F46E5' : '#94A3B8',
                            boxShadow: view === 'editor' ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 'none',
                            border: 'none',
                            '&:hover': {
                                backgroundColor: view === 'editor' ? '#FFFFFF' : 'transparent',
                                color: view === 'editor' ? '#4F46E5' : '#64748B',
                            }
                        }}
                    >
                        Compose
                    </Button>
                    <Button 
                        onClick={() => setView('pdf')}
                        sx={{ 
                            minWidth: '120px', 
                            padding: '6px 16px', 
                            fontSize: '13px', 
                            fontWeight: 500,
                            borderRadius: '9999px',
                            textTransform: 'none',
                            fontFamily: 'Poppins, sans-serif',
                            backgroundColor: view === 'pdf' ? '#FFFFFF' : 'transparent',
                            color: view === 'pdf' ? '#4F46E5' : '#94A3B8',
                            boxShadow: view === 'pdf' ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 'none',
                            border: 'none',
                            '&:hover': {
                                backgroundColor: view === 'pdf' ? '#FFFFFF' : 'transparent',
                                color: view === 'pdf' ? '#4F46E5' : '#64748B',
                            }
                        }}
                    >
                        {attachPDF ? <FontAwesomeIcon style={{ paddingRight: '4px', fontSize: '12px' }} icon={faCheck} /> : null}
                        Attach PDF
                    </Button>
                    {false && (
                        <Button color='primary' variant={view === 'all-fields' ? 'contained' : 'outlined'} sx={{ minWidth: '120px', padding: '4px 12px', fontSize: '12px', borderRadius: '6px 6px 0px 0px', borderBottom: 'none', textTransform: 'none', borderWidth: '1px' }} onClick={() => setView('all-fields')}>
                            All Fields
                        </Button>
                    )}
                    <Button 
                        onClick={() => setView('preview')}
                        sx={{ 
                            minWidth: '120px', 
                            padding: '6px 16px', 
                            fontSize: '13px', 
                            fontWeight: 500,
                            borderRadius: '9999px',
                            textTransform: 'none',
                            fontFamily: 'Poppins, sans-serif',
                            backgroundColor: view === 'preview' ? '#FFFFFF' : 'transparent',
                            color: view === 'preview' ? '#4F46E5' : '#94A3B8',
                            boxShadow: view === 'preview' ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 'none',
                            border: 'none',
                            '&:hover': {
                                backgroundColor: view === 'preview' ? '#FFFFFF' : 'transparent',
                                color: view === 'preview' ? '#4F46E5' : '#64748B',
                            }
                        }}
                    >
                        Email Preview
                    </Button>
                </Box>
                <Box sx={{ display: view === 'editor' ? 'block' : 'none' }}>
                    <OpportunityEditor
                    ref={editorRef}
                    sx={{ paddingBottom: '0px' }}
                    noBottomBorder={true}
                    initialContent={preface}
                    onFieldCreated={onFieldCreated}
                    onFieldClicked={onFieldClicked}
                    onTotalCreated={onTotalCreated}
                    onTotalClicked={onTotalClicked}
                    onTotalNeedsUpdate={onTotalNeedsUpdate}
                    onFieldsCreatedFromRate={onFieldsCreatedFromRate}
                    onBlur={onBlur}
                    onFieldDeleted={onFieldDeleted}
                    onFieldDeleteRequested={onFieldDeleteRequested}
                    toolbarStyle={{ borderRadius: '8px 0px 0px 0px' }}
                    onAddTemplate={!_.isEmpty(templates) ? onAddTemplate : null}
                    onAddTerms={!_.isEmpty(organization.terms) ? () => {
                        setDialogs(d => [...d, { id: uuidv4(), open: 'terms' }])
                    } : null}
                    transportationMethod={transportationMethod}
                    organization={organization}
                    onUploadImage={onUploadImage}
                    fieldLabel={() => currencyToText(organization.defaults?.currency || 'USD', true, 0)}
                    totalLabel={() => currencyToText(organization.defaults?.currency || 'USD', true, 0)}
                    editable={!status || status === 'draft'}
                    customToolbar={(!transportationMethod || _.isEmpty(products)) ? (
                        <Alert severity='warning'>
                            Please set transportation method and products to start building your quote.
                        </Alert>
                    ) : null}
                />
                </Box>
            </Paper>
            {view === 'pdf' && (
                <StructurePane
                    onPreview={() => setDialogs(d => [...d, { id: uuidv4(), open: 'preview' }])}
                    onDownloadPdf={onDownloadPdf}
                    downloadingPdf={downloadingPdf}
                    pdfError={pdfError}
                    attachPDF={attachPDF}
                    setAttachPDF={setAttachPDF}
                    offers={offers}
                    setOffers={setOffers}
                    products={products}
                    transportationMethod={transportationMethod}
                    exchangeRates={exchangeRates}
                    lockedExchangeRates={lockedExchangeRates}
                    buyingRates={buyingRates}
                    organization={organization}
                    preface={preface}
                    validationErrors={validationErrors}
                    errorFixMap={ERROR_FIX_MAP}
                />
            )}
            {view === 'all-fields' && (
                <AllFieldsPane
                    offers={offers}
                    products={products}
                    transportationMethod={transportationMethod}
                    exchangeRates={exchangeRates}
                    lockedExchangeRates={lockedExchangeRates}
                    organization={organization}
                    preface={preface}
                    onFieldClicked={onFieldClicked}
                />
            )}
            {view === 'preview' && (
                <Box sx={{ 
                    margin: '0px 16px', 
                    width: 'calc(100% - 32px)', 
                    padding: '24px',
                    backgroundColor: '#f8fafc',
                    display: 'flex',
                    justifyContent: 'center',
                    minHeight: '400px'
                }}>
                    <Box sx={{
                        maxWidth: '680px',
                        width: '100%',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        padding: '32px',
                        position: 'relative'
                    }}>
                        <Box
                            onClick={(e) => {
                                const link = e.target.closest('a');
                                if (link) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDialogs(d => [...d, {id: uuidv4(), open: 'preview' }]);
                                }
                            }}
                            dangerouslySetInnerHTML={{
                                __html: (() => {
                                    // If terms is explicitly set (even if empty array), use it. Otherwise use default from org.
                                    const _terms = (terms !== null && terms !== undefined) ? terms : (_.get(organization, `terms['price-quote-${transportationMethod}']`, null) || _.get(organization, 'terms[\'price-quote\']', null));
                                    const __terms = !!_terms && _.isString(_terms) ? [ { value: _terms } ] : _terms;
                                    return offerToText({
                                        type: 'one-off-free-text',
                                        offers: offers,
                                        preface: preface,
                                        epilogue: '',
                                        products: products,
                                        transportationMethod: transportationMethod,
                                        buyingRates: buyingRates,
                                        exchangeRates: exchangeRates,
                                        expanded: true,
                                        terms: __terms,
                                        lockedExchangeRates: lockedExchangeRates,
                                        organization: organization
                                    });
                                })()
                            }}
                            sx={{
                                '& pre': {
                                    fontFamily: 'Inter, Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                    fontSize: '14px',
                                    lineHeight: '1.6',
                                    color: '#0f172a',
                                    whiteSpace: 'pre-wrap',
                                    margin: 0,
                                    padding: 0,
                                    textAlign: 'left'
                                },
                                '& a': {
                                    display: 'inline',
                                    backgroundColor: 'transparent',
                                    color: '#3b82f6 !important',
                                    padding: '0',
                                    borderRadius: '0',
                                    textDecoration: 'underline !important',
                                    fontWeight: 400,
                                    fontSize: '12px',
                                    margin: '0',
                                    textAlign: 'left',
                                    width: 'auto',
                                    minWidth: 'auto',
                                    maxWidth: 'none',
                                    boxSizing: 'border-box',
                                    cursor: 'pointer',
                                    transition: 'color 0.2s',
                                    border: 'none',
                                    '&:hover': {
                                        color: '#2563eb !important',
                                        backgroundColor: 'transparent',
                                        textDecoration: 'underline !important'
                                    }
                                },
                                // Style body text (Hi, Best Regards) with softer color
                                '& pre > div:first-child': {
                                    color: '#334155 !important'
                                },
                                '& pre > div:last-child': {
                                    color: '#334155 !important'
                                },
                                // Style pricing lines - make service names bold and ensure proper spacing
                                '& pre > div:not(:first-child):not(:last-child)': {
                                    color: '#0f172a',
                                    marginBottom: '4px'
                                }
                            }}
                        />
                        {attachPDF && (
                            <Box sx={{
                                marginTop: '24px',
                                maxWidth: '280px',
                                backgroundColor: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '12px'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                    <FontAwesomeIcon icon={faFilePdf} style={{ fontSize: '24px', color: '#ef4444' }} />
                                    <Box>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#0f172a', lineHeight: 1.2 }}>
                                            {quotePdfFilename(opportunity)}
                                        </Typography>
                                        <Typography sx={{ fontSize: '11px', color: '#64748b', lineHeight: 1.2, marginTop: '2px' }}>
                                            1.2 MB
                                        </Typography>
                                    </Box>
                                </Box>
                                <Tooltip title={pdfError || 'Download the PDF that will be attached'} arrow placement='top'>
                                    <span>
                                        <IconButton
                                            size='small'
                                            // Downloads the real attachment. This used to open the offer page
                                            // with type=preview, which is the copy stamped PREVIEW - not the
                                            // file the customer receives.
                                            onClick={onDownloadPdf}
                                            disabled={downloadingPdf}
                                            sx={{
                                                color: '#3b82f6',
                                                '&:hover': {
                                                    backgroundColor: '#eff6ff'
                                                }
                                            }}
                                        >
                                            {downloadingPdf
                                                ? <CircularProgress size={16} />
                                                : <FontAwesomeIcon icon={faDownload} style={{ fontSize: '16px' }} />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            <Box sx={{ display: view === 'editor' ? 'block' : 'none', 
                    border: '1px solid #dadce0', 
                    borderTop: 'none',
                    borderRadius: '0px 0px 8px 8px',
                    padding: '16px',
                    margin: '0px 16px',
                    width: 'calc(100% - 32px)',
             }}>
                { terms !== null && terms !== undefined && !_.isEmpty(terms) && view === 'editor' && (
                    <Alert 
                        severity='info'
                        sx={{ fontSize: '12px', fontFamily: 'Poppins, sans-serif', '& .MuiAlert-message': { fontSize: '12px', fontFamily: 'Poppins, sans-serif' } }}
                        action={
                            <Button 
                                variant='contained' 
                                color='primary' 
                                size='small'
                                onClick={() => setDialogs(d => [...d, { id: uuidv4(), open: 'terms' }])}
                                disabled={status && status !== 'draft'}
                            >
                                Edit
                            </Button>
                        }
                    >
                        The following {pluralize('term', terms.length)} and {pluralize('condition', terms.length)} will be added to the bottom of the email: {terms.map((t, i) => (
                            <span key={t.name}>
                                <Tooltip title={<span dangerouslySetInnerHTML={{ __html: t.value }} />}>
                                    <span style={{ textDecoration: 'underline' }}>{t.name ? _.startCase(t.name) : 'Custom terms'}</span>
                                </Tooltip>
                                {i < terms.length - 2 ? ',' : i === terms.length - 1 ? '' : ', and '}
                            </span>
                        ))}
                    </Alert>
                )}
                { (terms === null || terms === undefined) && view === 'editor' && (
                    <Alert 
                        severity='info'
                        action={
                            <Button 
                                variant='contained' 
                                color='primary' 
                                size='small'
                                onClick={() => setDialogs(d => [...d, { id: uuidv4(), open: 'terms' }])}
                                disabled={status && status !== 'draft'}
                            >
                                Edit
                            </Button>
                        }
                    >
                        <Typography variant='caption' sx={{ fontSize: '13px' }}>
                            Using default terms & conditions{transportationMethod ? ` for ${transportationMethod}` : ''}.
                        </Typography>
                    </Alert>
                )}
                { terms !== null && terms !== undefined && _.isEmpty(terms) && view === 'editor' && (
                    <Alert 
                        severity='info'
                        action={
                            <Button 
                                variant='contained' 
                                color='primary' 
                                size='small'
                                onClick={() => setDialogs(d => [...d, { id: uuidv4(), open: 'terms' }])}
                                disabled={status && status !== 'draft'}
                            >
                                Edit
                            </Button>
                        }
                    >
                        <Typography variant='caption' sx={{ fontSize: '13px' }}>
                            No terms & conditions selected.
                        </Typography>
                    </Alert>
                )}
            </Box>
            {dialogs.map(dialog => {
                if (dialog.open === 'field') {
                    return (
                        <FieldSettingsDialog
                            key={dialog.id}
                            open={dialog.open === 'field'}
                            onClose={() => setDialogs(d => _.initial(d))}
                            field={dialog.field}
                            onTitleChange={(updatedField) => {
                                if (dialog.update && updatedField) {
                                    const offerForField = getOfferForField({ offers, field: updatedField });
                                    const section = offerForField?.sections?.find(s => s.offers.some(o => o.fields.some(f => f.id === updatedField.id)))?.id || offers[0]?.sections?.[0]?.id;
                                    if (section) {
                                        onFieldUpdated({ field: updatedField, section, update: dialog.update, buyingRates, offers });
                                    }
                                }
                            }}
                            onDone={({ field, section, currencyBulkUpdate, buyingRates, offers }) => {
                                field.displayCurrency = field.displayCurrency?.convert ? field.displayCurrency.to : (_.isString(field.displayCurrency) ? field.displayCurrency : null);
                                setBuyingRates(buyingRates);
                                setOffers(offers);
                                onFieldUpdated({ field, section, update: dialog.update, currencyBulkUpdate, buyingRates, offers })
                                if (dialog.onCallback) dialog.onCallback(field);
                                setDialogs(d => _.initial(d))
                            }}
                            products={products}
                            suppliers={suppliers}
                            sections={getOfferForField({ offers, field: dialog.field }) ? getOfferForField({ offers, field: dialog.field }).sections : offers[0].sections}
                            transportationMethod={transportationMethod}
                            organization={organization}
                            exchangeRates={exchangeRates}
                            lockedExchangeRates={lockedExchangeRates}
                            buyingRates={buyingRates}
                            offers={offers}
                            assignSection={true}
                        />
                    );
                } else if (dialog.open === 'total') {
                    return (
                        <TotalDialog
                            key={dialog.id}
                            open={dialog.open === 'total'}
                            onClose={() => {
                                setDialogs(d => _.initial(d));
                                dialog.total.update(null, null);
                            }}
                            total={dialog.total}
                            checkedFields={dialog.total.checkedFields}
                            setCheckedFields={checkedFields => {
                                const { str, suppliersStr, margin, costStr, profitStr } = getTotalForFields({ fields: checkedFields, offers, products, buyingRates, transportationMethod, exchangeRates, lockedExchangeRates, organization });
                                dialog.total.update(str, checkedFields, margin, profitStr, costStr, suppliersStr);
                            }}
                            offers={offers}
                            products={products}
                            transportationMethod={transportationMethod}
                            exchangeRates={exchangeRates}
                            lockedExchangeRates={lockedExchangeRates}
                            organization={organization}
                            buyingRates={buyingRates}
                            preface={preface}
                            onAddField={() => onTotalFieldCreated({ id: uuidv4() })}
                            onEditField={(e) => {
                                setDialogs(d => [...d, { id: uuidv4(), open: 'field', field: e, update: (field) => {} }]);
                            }}
                        />
                    )
                } else if (dialog.open === 'addrate') {
                    return (
                        <AddRateDialog
                            key={dialog.id}
                            open={dialog.open === 'addrate'}
                            offers={offers}
                            rate={dialog.rate}
                            onClose={() => setDialogs(d => _.initial(d))}
                            onConfirm={({ margin, addMetaData, offerType, newOfferName, existingOffer, canceled }) => {
                                dialog.onMargin({ margin, addMetaData, offerType, newOfferName, existingOffer, canceled });
                                setDialogs(d => _.initial(d));
                            }}
                        />
                    );
                } else if (dialog.open === 'add-template') {
                    return (
                        <AddTemplateDialog
                            key={dialog.id}
                            open={dialog.open === 'add-template'}
                            organization={organization}
                            products={products}
                            transportationMethod={transportationMethod}
                            routes={routes}
                            offers={offers}
                            setOffers={setOffers}
                            setBuyingRates={setBuyingRates}
                            callback={dialog.callback}
                            onClose={() => setDialogs(d => _.initial(d))}
                            opportunities={opportunities?.filter(o => o.attributes?.template && o.transportationMethod === transportationMethod && o.type === 'one-off-free-text').map(o => ({ 
                                ...o, 
                                title: o.title || `${accounts.find(a => a.id === o.account.id)?.name || '(No Title)'} - #${readableId(o)} - ${dayjs(o.createdAt).format('DD/MM/YYYY')}`
                            }))}
                        />
                    );
                } else if (dialog.open === 'terms') {
                    return (
                        <TermsDialog
                            key={dialog.id}
                            open={dialog.open === 'terms'}
                            onClose={() => setDialogs(d => _.initial(d))}
                            onSave={(terms) => setTerms(terms)}
                            terms={terms}
                            transportationMethod={transportationMethod}
                            organization={organization}
                        />
                    )
                } else if (dialog.open === 'preview') {
                    return (
                        <PreviewDialog
                            key={dialog.id}
                            open={dialog.open === 'preview'}
                            onClose={() => setDialogs(d => _.initial(d))}
                            opportunityId={opportunityId}
                            saveSuccess={saveSuccess}
                            saving={saving}
                            saveError={saveError}
                            saveErrorDescription={saveErrorDescription}
                            onSave={onSave}
                            organization={organization}
                            accesstoken={accesstoken}
                            validationErrors={validationErrors}
                        />
                    );
                } else {
                    return null;
                }
            })}
            <Dialog
                open={!!deleteFieldConfirm}
                onClose={() => {
                    // ESC or outside click: undo the editor deletion, don't take any action
                    editorRef.current?.commands?.undo?.();
                    setDeleteFieldConfirm(null);
                }}
            >
                <DialogTitle>Deleted content includes saved costs</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        We noticed you deleted content from the email that includes saved costs.
                    </DialogContentText>
                    <DialogContentText>
                        {deleteFieldConfirm?.sectionFields?.length > 0
                            ? 'Do you want to delete these costs completely from everywhere, or still keep them in your offer PDF?'
                            : 'Do you want to delete these costs completely from everywhere, or still keep them in your total?'}
                    </DialogContentText>
                    <Box sx={{ mt: 2 }}>
                        {[...(deleteFieldConfirm?.sectionFields || []), ...(deleteFieldConfirm?.totalFields || []), ...(deleteFieldConfirm?.deletableFields || [])].map(f => (
                            <Typography key={f.id} variant="body2" sx={{ fontWeight: 500, color: '#334155', py: 0.25 }}>
                                &bull; {f.label}
                            </Typography>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        // Keep in PDF/total: preserve all fields in offers, don't delete anything
                        setDeleteFieldConfirm(null);
                    }}>
                        {deleteFieldConfirm?.sectionFields?.length > 0 ? 'Keep in PDF' : 'Keep in total'}
                    </Button>
                    <Button variant="contained" onClick={() => {
                        // Delete everywhere: remove all fields
                        deleteFieldConfirm.ids.forEach(id => onFieldDeleted({ id }));
                        setDeleteFieldConfirm(null);
                    }}>
                        Delete everywhere
                    </Button>
                </DialogActions>
            </Dialog>
            <QuotePdfNotice notice={pdfNotice} onClose={dismissPdfNotice} />
        </Box>
    );
}

export default OpportunityOneOffFreeTextBuilder;
export { TotalDialog };

function TotalDialog(props) {
    const { open, onClose, offers, buyingRates, checkedFields, setCheckedFields, products, transportationMethod, 
        exchangeRates, lockedExchangeRates, organization, onAddField, onEditField, preface } = props;

    const [ innerCheckedFields, setInnerCheckedFields ] = useState(checkedFields);
    const [ search, setSearch ] = useState('');
    const [ showDisconnectedFields, setShowDisconnectedFields ] = useState(false);

    useEffect(() => {
        setInnerCheckedFields(checkedFields);
    }, [ checkedFields, open ]);

    const allFields = _(offers).map(o => o.sections).flatten().map(s => s.offers).flatten().map(o => o.fields).flatten().value();

    const disconnectedFields = allFields.filter(f => {
        if (!(_(offers).map(o => o.sections?.[0].offers?.[0].fields).flatten().value().map(f => f.id).includes(f.id))) return false;
        return !(preface || '').toLowerCase().includes(f.id.toLowerCase());
    }).map(f => f.id);

    const showFields = allFields.filter(f => showDisconnectedFields ? true : !disconnectedFields.includes(f.id));

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Total Field</DialogTitle>
            <DialogContent>
                <Typography variant='body1'>
                    Total fields are used to calculate the total value of some or all the fields in the offer. They are not added to the selling price.
                </Typography>
                <Typography variant='body1'>
                    Select the fields to include in the total:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', mt: 1 }}>
                        <TextField
                            size='small'
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            sx={{ flex: 1 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <FormControlLabel
                            control={<Checkbox 
                                size='small'
                                checked={showDisconnectedFields} 
                                onChange={e => {
                                    setShowDisconnectedFields(!showDisconnectedFields);
                                }}
                            />}
                            label={<Typography variant='caption'>Show disconnected fields</Typography>}
                        />
                    </Box>
                    <FormControlLabel
                        control={<Checkbox 
                            size='small'
                            checked={_.every(showFields, f => innerCheckedFields.includes(f.id))} 
                            indeterminate={_.some(showFields, f => innerCheckedFields.includes(f.id)) && !_.every(showFields, f => innerCheckedFields.includes(f.id))}
                            onChange={e => {
                                if (_.every(showFields, f => innerCheckedFields.includes(f.id))) {
                                    setInnerCheckedFields([]);
                                } else {
                                    setInnerCheckedFields(showFields.map(f => f.id));
                                }
                            }}
                        />}
                        label={<Typography variant='caption'>All fields</Typography>}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '300px', overflowY: 'auto' }}>
                        {/* A field need not have a title - one priced but never named is shown
                            by its amount instead, which the row below already allows for. Only
                            this search forgot, and a single untitled field in the quote took
                            the whole page down with it. */}
                        {showFields.filter(f => (f.title || '').toLowerCase().includes(search.toLowerCase())).map(f => {
                                const title = (() => {
                                    if (!products) {
                                        return f.title;
                                    }

                                    const cost = getTotalRates({
                                        field: f,
                                        fieldBuyingRates: f.buyingRates,
                                        products,
                                        buyingRates,
                                        transportationMethod,
                                        exchangeRates,
                                        organization,
                                    });

                                    const { sum } = calcFieldValue({
                                        field: f,
                                        sections: offers[0].sections,
                                        products,
                                        transportationMethod,
                                        exchangeRates,
                                        lockedExchangeRates,
                                        organization,
                                        cost
                                    });

                                    const s = _.map(sum, (v, k) => currencyToText(k, true, v.toLocaleString({ maximumFractionDigits: 2 }))).join(', ');
                                    if (f.title) return `${f.title} (${s})`;
                                    return s;
                                })();

                                const subtitle = (() => {
                                    const parts = [];
                                    if (!_.isEmpty(f.buyingRates)) {
                                        const rates = f.buyingRates.map(br => buyingRates.find(b => b.id === br.rateId));
                                        parts.push(`${oxfordJoin(_.uniq(_.map(rates, r => `${r.supplier.organization} - ${r.product.type}`)))}`);
                                    }
                                    if (_.isEmpty(f.buyingRates) && (f.type === 'per-unit-type')) {
                                        const productIds = _.keys(f.values).filter(k => f.values[k].value || f.values[k].formula);
                                        parts.push(`Priced for ${oxfordJoin(_.map(productIds, id => products.find(p => p.id === id)?.type))}`);
                                    }

                                    return _.isEmpty(parts) ? null : parts.join(' | ');
                                })();

                                return (
                                    <TotalField 
                                        key={f.id} 
                                        title={title} 
                                        subtitle={subtitle}
                                        disconnected={disconnectedFields.includes(f.id)}
                                        checked={innerCheckedFields.includes(f.id)} 
                                        setChecked={checked => setInnerCheckedFields(innerCheckedFields.includes(f.id) ? innerCheckedFields.filter(id => id !== f.id) : [...innerCheckedFields, f.id])} 
                                        onEdit={() => onEditField(f)}
                                    />
                                );
                            })}
                    </Box>
                    <Button variant='text' onClick={onAddField} sx={{ textAlign: 'start', textTransform: 'none', fontSize: '9px', padding: '0px', justifyContent: 'flex-start' }}>
                        Add field not in the email (for example, when showing only totals in the email)
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={() => {
                    setCheckedFields(innerCheckedFields);
                    onClose();
                }} sx={{ fontFamily: 'Poppins, sans-serif' }}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}

function TotalField(props) {
    const { title, subtitle, checked, setChecked, onEdit, disconnected } = props;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
            <FormControlLabel
                control={<Checkbox checked={checked} onChange={() => setChecked(!checked)} />}
                label={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                            {disconnected && <FontAwesomeIcon icon={faChainBroken} style={{ fontSize: '10px' }} />}
                            <Typography variant='body1' sx={{ color: disconnected ? 'text.secondary' : 'inherit' }}>{title}</Typography>
                            <IconButton onClick={(e) => { e.stopPropagation(); onEdit(); }}><FontAwesomeIcon icon={faPencil} /></IconButton>
                        </Box>
                        {subtitle && <Typography variant='caption' sx={{ color: 'text.secondary' }}>{subtitle}</Typography>}
                    </Box>
                }
            />
        </Box>
    );
}

function getTotalForFields({fields, offers, products, buyingRates, transportationMethod, exchangeRates, lockedExchangeRates, organization}) {
    const acc = _.reduce(fields, (acc, id) => {

        const a = offers.map(o =>
            o.sections.map(s =>
                s.offers.map(so =>
                    so.fields.map(f => ({ field: f, offer: o, section: s }))))).flat().flat().flat();
        
        const { field } = a.find(f => f.field.id === id) || {};

        if (!field) return acc;

        const summary = getSummaryForField({ field, offers, products, buyingRates, transportationMethod, 
            exchangeRates, lockedExchangeRates, organization });

        _.forEach(summary.sum, (v, k) => {
            if (!acc.totals[k]) acc.totals[k] = 0;
            acc.totals[k] += v;
        });

        _.forEach(summary.costs, (v, k) => {
            if (!acc.costs[k]) acc.costs[k] = 0;
            acc.costs[k] += v;
        });

        acc.costsNormalized += summary.costsNormalized;

        acc.suppliers = _.uniq(_.compact([...acc.suppliers, ...summary.suppliers]));
        return acc;
    }, { totals: {}, costs: {}, costsNormalized: 0, suppliers: [] });

    const formatter = Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
    const currency = _.keys(acc.totals)[0] || organization.defaults?.currency;

    if (_.isEmpty(acc.totals)) acc.totals[currency] = 0;

    const str = oxfordJoin(_.map(acc.totals, (v, k) => currencyToText(k, true, v.toLocaleString({ maximumFractionDigits: 2 }))));
    const sellingNormalized = _.sum(_.map(acc.totals, (v, k) => v / exchangeRates[k]));
    const margin = sellingNormalized ? (sellingNormalized - acc.costsNormalized) / sellingNormalized * 100 : 0;
    const profit = (sellingNormalized - acc.costsNormalized) * exchangeRates[currency];

    const costStr = oxfordJoin(_.map(acc.costs, (v, k) => currencyToText(k, true, formatter.format((v || 0)))));
    const profitStr = currencyToText(currency, true, formatter.format(profit));
    const suppliersStr = oxfordJoin(acc.suppliers);

    return { sum: acc.totals, str, costs: acc.costs, suppliers: acc.suppliers, suppliersStr, margin, costStr, profitStr };
}

function getSummaryForField({ field, offers, products, buyingRates, transportationMethod, 
    exchangeRates, lockedExchangeRates, organization }) {

    const allFieldsWithOffers = _(offers).map(o => {
        return o.sections.map(s => s.offers).flat().map(so => so.fields).flat().map(f => ({ field: f, offer: o }));
    }).flatten().value();

    const costs = getTotalRates({
        field,
        fieldBuyingRates: field.buyingRates,
        products,
        buyingRates,
        transportationMethod,
        exchangeRates,
        organization,
        skipSumCurrencies: true
    });

    const costsNormalized = _.sum(_.map(costs, (val, currency) => val / exchangeRates[currency])) || 0;

    const calcValue = calcFieldValue({
        field,
        sections: allFieldsWithOffers.find(f => f.field.id === field.id).offer.sections,
        products,
        transportationMethod,
        exchangeRates,
        lockedExchangeRates,
        organization,
        fieldBuyingRates: field.buyingRates,
        buyingRates,
    });
    const sellingNormalized = _.sum(_.map(calcValue.sum, (v, k) => v / exchangeRates[k]));
    const currency = _.keys(calcValue.sum)[0] || organization.defaults?.currency;
    const str = currencyToText(currency, true, (calcValue.sum[currency] || 0).toLocaleString({ maximumFractionDigits: 2 }));

    let profit = Math.round(((sellingNormalized - costsNormalized) * exchangeRates[currency]) * 10000) / 10000;
    let margin = Math.round(((sellingNormalized - costsNormalized) / sellingNormalized * 100) * 10000) / 10000;

    if (profit === -0) profit = 0;
    if (margin === -0) margin = 0;

    const formatter = Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

    const costStr = oxfordJoin(_.map(costs, (v, c) => currencyToText(c, true, formatter.format((v || 0)))));
    const profitStr = currencyToText(currency, true, formatter.format(profit));
    const suppliers = _.compact(_.uniqBy(field.buyingRates, 'rateId').map(r => buyingRates.find(br => br.id === r.rateId)?.supplier?.organization));
    const suppliersStr = oxfordJoin(suppliers);

    return { sum: calcValue.sum, str, margin, profitStr, costs, costsNormalized, costStr, suppliers, suppliersStr };
}

function AddRateDialog(props) {
    const { open, onClose, onConfirm, offers, rate } = props;
    const [ margin, setMargin ] = useState(0);
    const [ addMetaData, setAddMetaData ] = useState(false);
    const [ offerType, setOfferType ] = useState(_.isEmpty(offers) ? 'new' : 'existing');
    const [ newOfferName, setNewOfferName ] = useState('');
    const [ existingOffer, setExistingOffer ] = useState(_.first(offers)?.id);
    const [ step, setStep ] = useState(0);

    useEffect(() => {
        if (rate) {
            setNewOfferName(`${rate.supplier.organization} - ${rate.product.type} - ${rate.source?.text || 'Everywhere'} to ${rate.destination?.text || 'Everywhere'}`);
        }
    }, [ rate ]);

    return (
        <Dialog open={open} sx={{ zIndex: 1000000 }} maxWidth='sm' fullWidth>
            <DialogTitle>Add Fields from Rate</DialogTitle>
            <DialogContent>
                <Stepper activeStep={step} alternativeLabel sx={{ mb: 2 }}>
                    <Step>
                        <StepLabel>Margin</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Rate Information</StepLabel>
                    </Step>
                </Stepper>
                {step === -1 && (
                    <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <MenuItem
                                sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}
                                onClick={() => setOfferType('new')}
                            >
                                <FontAwesomeIcon icon={offerType === 'new' ? faCircle : faCircleLight} />
                                <Typography variant='caption'>Add pricing as a new offer{offerType === 'new' ? ', named:' : ''}</Typography>
                            </MenuItem>
                            {offerType === 'new' && (
                                <Box sx={{ pl: 5 }}>
                                    <TextField
                                        margin='dense'
                                        label='New Offer Name'
                                        fullWidth
                                        value={newOfferName}
                                        onChange={(e) => setNewOfferName(e.target.value)}
                                    />
                                </Box>
                            )}
                            <MenuItem
                                sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}
                                onClick={() => setOfferType('existing')}
                            >
                                <FontAwesomeIcon icon={offerType === 'existing' ? faCircle : faCircleLight} />
                                <Typography variant='caption'>Add pricing to existing offer{offerType === 'existing' ? ':' : ''}</Typography>
                            </MenuItem>
                            {offerType === 'existing' && (
                                <Select 
                                    value={existingOffer} 
                                    onChange={(e) => setExistingOffer(e.target.value)} 
                                    sx={{ ml: 5 }}
                                    MenuProps={{
                                        style: {
                                            zIndex: 1000000,
                                        }
                                    }}>
                                    {offers.map(o => <MenuItem key={o.id} value={o.id}>{o.title}</MenuItem>)}
                                </Select>
                            )}
                        </Box>
                    </>
                )}
                {step === 0 && (
                    <>
                        <Typography component='div' variant='caption' sx={{ mt: 1 }}>
                            Please enter the margin percentage to add to all rates before adding them:
                        </Typography>
                        <TextField
                            sx={{ mt: 2 }}
                            margin='dense'
                            label='Margin (%)'
                            fullWidth
                            value={margin}
                            onChange={(e) => setMargin(e.target.value)}
                        />
                    </>
                )}
                {step === 1 && (
                    <>
                        <FormControlLabel
                            control={<Checkbox checked={addMetaData} onChange={e => {
                                setAddMetaData(!addMetaData);
                            }} />}
                            label={<Typography variant='caption'>Add rate information (transit time, validity, etc.)</Typography>}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setStep(step - 1)} disabled={step === 0}>Back</Button>
                <Box sx={{ flex: 1 }} />
                <Button color='secondary' onClick={() => { onConfirm({ canceled: true }); onClose(); }}>Cancel</Button>
                <Button onClick={() => {
                    if (step < 1) {
                        setStep(step + 1);
                        return;
                    }
                    onConfirm({ addMetaData, margin: (parseFloat(margin) || 0) / 100, offerType, newOfferName, existingOffer });
                    onClose();
                }}>{step === 1 ? 'Add' : 'Next'}</Button>
            </DialogActions>
        </Dialog>
    );
}

function AllFieldsPane(props) {
    const { offers, products, transportationMethod, exchangeRates, lockedExchangeRates, organization, preface, onFieldClicked } = props;

    const allFields = _(offers).map(o => o.sections).flatten().map(s => s.offers).flatten().map(o => o.fields).flatten().value();
    const theme = useTheme();

    function getFieldUsage(field) {
        const usages = [];

        // Check preface for field usage
        const parser = new DOMParser();
        const doc = parser.parseFromString(preface || '', 'text/html');

        // Check direct field usage
        doc.querySelectorAll('span[data-field="true"]').forEach(span => {
            if (span.getAttribute('id') === field.id) {
                usages.push('Field in the email');
            }
        });

        // Check total usage
        doc.querySelectorAll('span[data-total="true"]').forEach(span => {
            const chargeIds = JSON.parse(span.getAttribute('data-charge-ids') || '[]');
            if (chargeIds.includes(field.id)) {
                usages.push(`Total "${span.textContent}"`);
            }
        });

        // Check PDF usage
        const offer = offers.find(o => 
            o.sections.some(s => 
                s.offers.some(so => 
                    so.fields.some(f => f.id === field.id)
                )
            )
        );
        const section = offer?.sections.find(s =>
            s.offers.some(so =>
                so.fields.some(f => f.id === field.id)
            )
        );

        if (section && section.title !== '***UNASSIGNED***') {
            usages.push(`PDF (${offer.title} - ${section.title})`);
        }

        return usages.length ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {usages.map(u => <Typography variant='body2' sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }} key={u}><FontAwesomeIcon style={{ fontSize: '5px' }} icon={faCircle} /> {u}</Typography>)}
            </Box>
        ) : (
            <Typography variant='caption' color='error' sx={{ fontStyle: 'italic' }}>
                <FontAwesomeIcon icon={faWarning} /> Not used (will be deleted next time you save)</Typography>
        );
    }

    return (
        <Table sx={{ border: `1px solid ${theme.palette.border1}`, margin: '0px 16px', width: 'calc(100% - 32px)' }}>
            <TableHead>
                <TableRow>
                    <TableCell>Field Name</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Used In</TableCell>
                    <TableCell>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {allFields.map(field => {

                    const value = calcFieldValue({
                        field,
                        products,
                        transportationMethod,
                        exchangeRates,
                        lockedExchangeRates,
                        organization
                    });

                    return (
                        <TableRow key={field.id}>
                            <TableCell>{field.title || '(No Label)'}</TableCell>
                            <TableCell>
                                {_.map(value.sum, (v, k) =>
                                    currencyToText(k, true, v.toLocaleString({ maximumFractionDigits: 2 }))
                                ).join(', ')}
                            </TableCell>
                            <TableCell>
                                {getFieldUsage(field)}
                            </TableCell>
                            <TableCell>
                                <IconButton onClick={() => onFieldClicked({ id: field.id })} color='primary'>
                                    <FontAwesomeIcon icon={faPencil} />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}

function AddTemplateDialog(props) {
    const { open, onClose, organization, transportationMethod, products, routes, offers, setOffers, 
        setBuyingRates, callback, opportunities } = props;

    function onAddOffer({ offers: newOffers, preface: _preface, convertMap = {} }) {
        let preface = _preface || '';
        _.forEach(convertMap, (destId, srcId) => preface = preface.replaceAll(srcId, destId));
        let structuredOffers = newOffers.map(newOffer => {
            const sections = (() => {
                if (newOffer.sections[0].title !== '***UNASSIGNED***') {
                    return [{ title: '***UNASSIGNED***', offers: [{ fields: [] }] }, ...newOffer.sections];
                }
                return [...newOffer.sections];
            })();
            return { ...newOffer, sections, title: newOffer.title || 'New Offer' };
        });
        setOffers(offers => [...offers, ...structuredOffers]);
        callback({ preface, newOffers: structuredOffers });
        onClose();
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle>Add Template</DialogTitle>
            <DialogContent>
                <AddTemplate
                    showAddGroupAnimation={false}
                    forms={organization.forms}
                    products={products}
                    routes={routes}
                    offers={offers}
                    addOffer={onAddOffer}
                    setBuyingRates={setBuyingRates}
                    type='opportunity-email-template'
                    transportationMethod={transportationMethod}
                    noDragDrop={true}
                    noScratch={true}
                    opportunities={opportunities}
                />
            </DialogContent>
        </Dialog>
    );
}

function updateFieldValues({ offers, field, products, transportationMethod, exchangeRates, lockedExchangeRates, organization, buyingRates, editorRef }) {
    const summary = getSummaryForField({ field, offers, products, buyingRates, transportationMethod, exchangeRates, lockedExchangeRates, organization });

    const section = offers.map(o => o.sections).flat().find(s => s.offers.flatMap(so => so.fields).find(f => f.id === field.id));
    const sectionTitle = section?.title || '***UNASSIGNED***';

    editorRef?.current?.updateFieldValue(field.id, summary.str, summary.margin, summary.profitStr, summary.costStr, summary.suppliersStr, field.title, sectionTitle === '***UNASSIGNED***' ? '' : sectionTitle);
}

function getOfferForField({ offers, field }) {
    return offers.find(o => o.sections.flatMap(s => s.offers).flatMap(so => so.fields).find(f => f.id === field.id));
}

function PreviewDialog(props) {
    const { open, onClose, opportunityId, organization, accesstoken, onSave, validationErrors = [] } = props;
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!open) {
            setError(null);
            setLoading(true);
            return;
        }
        (async () => {
            try {
                // Try to save first, but continue even if it fails (might have validation errors)
                const res = await onSave();
                if (!res) {
                    console.warn('[PreviewDialog] onSave returned false (likely validation errors), but attempting to open preview anyway');
                }
                
                // Always try to get preview URL, even if save had validation errors
                const { data: previewUrl } = await axios(`/api/opportunities/${opportunityId}/offer-url?accesstoken=${accesstoken}&organizationId=${organization.id}&type=preview&minimized=false`);
                if (previewUrl) {
                    const previewWindow = window.open(`${getBaseUrl()}/offers?temporaryAccesstoken=${previewUrl}&type=preview&pdf=true`, '_blank');
                    if (!previewWindow) {
                        console.error('[PreviewDialog] window.open was blocked by browser');
                        setError('Please allow popups for this site to view the preview.');
                        setLoading(false);
                    } else {
                        console.log('[PreviewDialog] Preview opened successfully');
                        onClose();
                    }
                } else {
                    console.error('[PreviewDialog] No preview URL returned from API');
                    setError('We just need a few more details to generate your preview. Please complete the Shipment Details section to continue.');
                    setLoading(false);
                }
            } catch (e) {
                console.error('[PreviewDialog] Error opening preview:', e);
                // Show user-friendly error message without technical details
                setError('We just need a few more details to generate your preview. Please complete the Shipment Details section to continue.');
                setLoading(false);
            }
        })();
    }, [ open ]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                        Preview Offer
                    </Box>
                    <IconButton onClick={onClose}>
                        <FontAwesomeIcon icon={faXmark} />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '200px' }}>
                        <CircularProgress size={48} />
                    </Box>
                ) : error ? (
                    <Box sx={{ 
                        py: 2,
                        px: 2,
                        backgroundColor: '#f0f9ff',
                        border: '1px solid #bae6fd',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2
                    }}>
                        <Box sx={{ 
                            color: '#0284c7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '24px',
                            height: '24px',
                            mt: '2px'
                        }}>
                            <FontAwesomeIcon icon={faCircleInfo} style={{ fontSize: '20px' }} />
                        </Box>
                        <Typography sx={{ 
                            color: '#0c4a6e',
                            fontSize: '14px',
                            lineHeight: '1.5',
                            flex: 1
                        }}>
                            {error.split('Shipment Details').map((part, index, array) => 
                                index === array.length - 1 ? part : (
                                    <React.Fragment key={index}>
                                        {part}
                                        <strong style={{ fontWeight: 600 }}>Shipment Details</strong>
                                    </React.Fragment>
                                )
                            )}
                        </Typography>
                    </Box>
                ) : null}
            </DialogContent>
            {error && (
                <DialogActions>
                    <Button onClick={onClose} variant='contained' color='primary'>
                        OK
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
}